import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
} from '@wegroup/design-system';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Inputs } from '../../../../types/Enums';
import { GbiCode } from '../../../../types/CompanyScanTypes';
import { getFlowsDataTestId } from '../../../../utils/testUtils';
import GbiCodeList from './GbiCodeList';
import GbiCodeSearchInput from './GbiCodeSearchInput';
import GbiCodeTags from './GbiCodeTags';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  value: GbiCode[];
  onChange: (gbiCodes: GbiCode[]) => void;
}

export interface FormValues {
  searchQuery: string;
}

const GbiCodeModal = ({ isOpen, onClose, value, onChange }: Props) => {
  const { t } = useTranslation();
  const [gbiCodes, setGbiCodes] = useState<GbiCode[]>(value);

  const { register, watch, setValue } = useForm<FormValues>({
    defaultValues: {
      searchQuery: '',
    },
  });

  const handleAdd = (gbi: GbiCode) => {
    setGbiCodes([...gbiCodes, gbi]);
  };

  const handleOnRemove = (gbiCode: GbiCode) => {
    setGbiCodes((gbiCodes) =>
      gbiCodes.filter(({ code }) => code !== gbiCode.code),
    );
  };

  const handleOnApply = () => {
    onChange(gbiCodes);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} scrollBehavior="inside" size="3xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t('FLOWS_COMPANY-SCAN_GBI-INPUT_MODAL_TITLE')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody as={VStack} spacing="6" align="stretch">
          <GbiCodeSearchInput register={register} setValue={setValue} />
          {gbiCodes.length > 0 && (
            <GbiCodeTags gbiCodes={gbiCodes} onRemove={handleOnRemove} />
          )}

          <GbiCodeList
            gbiCodes={gbiCodes}
            watch={watch}
            onAdd={handleAdd}
            onRemove={handleOnRemove}
          />
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            {t('CANCEL')}
          </Button>
          <Button
            onClick={handleOnApply}
            data-test-id={getFlowsDataTestId(
              Inputs.GOUDSE_SBI_CODE,
              'APPLY-BUTTON',
            )}
          >
            {' '}
            {t('APPLY')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default GbiCodeModal;
